import { TRIZ_PARAMETERS, CONTRADICTION_MATRIX } from '../data/trizMatrix';
import { TRIZ_PRINCIPLES as LOCAL_TRIZ_PRINCIPLES } from '../data/trizPrinciples';

// Firestoreではなくローカルデータを使用（高速・安定）
const getTrizLaws = async () => {
  return [...LOCAL_TRIZ_PRINCIPLES].sort((a, b) =>
    String(a.id || "").localeCompare(String(b.id || ""), undefined, { numeric: true })
  );
};

export const analyzeProblem = async (problemData, userProfile = null) => {
  const TRIZ_PRINCIPLES = await getTrizLaws();
  const principleScores = new Map();
  TRIZ_PRINCIPLES.forEach(p => {
    principleScores.set(String(p.id), { principle: p, score: 0, reasons: [] });
  });
  const { improvingFactor, worseningFactor, description } = problemData;
  if (improvingFactor && worseningFactor) {
    const matrixKey = `${improvingFactor}-${worseningFactor}`;
    const recommendedIds = CONTRADICTION_MATRIX[matrixKey];
    if (recommendedIds?.length > 0) {
      const paramMap = new Map(TRIZ_PARAMETERS.map(p => [String(p.id), p.name]));
      const improvingName = paramMap.get(improvingFactor);
      const worseningName = paramMap.get(worseningFactor);
      recommendedIds.forEach(id => {
        const entry = principleScores.get(String(id));
        if (entry) {
          entry.score += 20;
          entry.reasons.push(`矛盾「${improvingName} vs ${worseningName}」の解決に有効`);
        }
      });
    }
  }
  const textToAnalyze = `${description}`.toLowerCase();
  TRIZ_PRINCIPLES.forEach(p => {
    const entry = principleScores.get(String(p.id));
    if (!entry) return;
    const principleText = `${p.name} ${p.description}`.toLowerCase();
    if (textToAnalyze.split(' ').some(word => word.length > 1 && principleText.includes(word))) {
        entry.score += 2;
        entry.reasons.push(`問題文のキーワードと関連`);
    }
    const applicationText = [...(p.examples || []), ...(p.applications || [])].join(' ').toLowerCase();
    if (textToAnalyze.split(' ').some(word => word.length > 1 && applicationText.includes(word))) {
        entry.score += 5;
        entry.reasons.push(`具体的な応用例と関連`);
    }
  });
  const sortedPrinciples = Array.from(principleScores.values()).filter(p => p.score > 0).sort((a, b) => b.score - a.score);
  let finalRecommendations = sortedPrinciples.slice(0, 3);
  if (finalRecommendations.length === 0) {
    finalRecommendations = [1, 5, 13].map(id => {
        const p = principleScores.get(String(id));
        if (p) p.reasons.push('汎用的な解決策として推奨');
        return p;
    }).filter(Boolean);
  }
  const recommendationsWithAdvice = finalRecommendations.map(p => {
    const base = p.principle;
    // ローカルデータのフィールドをカードが期待する形式にマッピング
    const principle = {
      ...base,
      reason: p.reasons.join(', '),
      reasons: p.reasons,
      // descriptionをdefinitionとしても使えるように
      definition: base.definition || base.description,
      // organizationApplication・talentApplicationをexamplesに変換
      examples: base.examples || [
        base.organizationApplication,
        base.talentApplication,
      ].filter(Boolean),
      // cognitiveSynergyをstageに変換
      stage: base.stage || {
        beginner: base.cognitiveSynergy?.[0] ? `「${base.cognitiveSynergy[0]}」を意識して取り組み始める段階` : null,
        intermediate: base.cognitiveSynergy?.[1] ? `「${base.cognitiveSynergy[1]}」も組み合わせて実践できる段階` : null,
        advanced: `${base.organizationApplication || ''}の観点で高度に応用できる段階`,
      },
    };
    if (userProfile && userProfile.neurodiversityProfile) {
        const { thinkingStyle } = userProfile.neurodiversityProfile;
        const tsValue = thinkingStyle?.value || 50;
        let advice = null;
        if (tsValue > 65) {
            if ([1, 4, 5, 6, 13, 14, 15, 17, 22, 29, 30].includes(Number(principle.id))) {
                advice = `あなたの得意な「発散的思考」を活かし、この原理の応用例を制限なくブレインストーミングしてみましょう。`;
            }
        } 
        else if (tsValue < 35) {
            if ([2, 3, 7, 9, 10, 16, 20, 25].includes(Number(principle.id))) {
                advice = `あなたの得意な「収束的思考」を使い、この原理を適用する具体的な手順をロジカルに書き出してみてください。`;
            }
        }
        if(advice) {
            principle.personalizedAdvice = advice;
        }
    }
    return principle;
  });
  return {
    analysis: problemData,
    recommendations: recommendationsWithAdvice
  };
};